import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { TLegacyDriver } from "../../../api/types/Driver";
import svgIcons from "../../../assets/svg/svgIcons";
import TripDetails from "../tripDetails";
import { TripSummaryChips } from "../tripSummaryChips";
import { DaySummary, summariseDay } from "../util/tripSummary";
import { isNonTrip } from "./tripClassification";
import { tripDisplayNumbers } from "./tripNumbering";
import { DateGroup } from "./useMultiDateTripData";

export const tripKeyFor = (dateKey: string, tripIndex: number) =>
  `${dateKey}__${tripIndex}`;

type Props = {
  dateGroups: DateGroup[];
  selectedTripKeys: Set<string>;
  onToggleTripKey: (tripKey: string, checked: boolean) => void;
  onSetAllSelected: (selected: boolean) => void;
  assignedDriver?: TLegacyDriver;
  replayingTripKey: string | null;
  replayPercentage: number | null;
  isReplaying: boolean;
  onReplayEvents: (tripKey: string) => void;
  onReplayAll: () => void;
  // hover driven from the map: highlights the row, and scrolls to it when
  // autoScrollOnHover is set
  hoveredTripKey: string | null;
  onHoverTrip: (tripKey: string | null) => void;
  autoScrollOnHover: boolean;
  // display order only, from the Trips header's sort control: "desc" puts
  // the most recent day (and its most recent trip) first. Nothing about the
  // underlying data moves - day colours, trip numbers and trip keys all
  // stay attached to the trip they belong to, so the map and the list can't
  // disagree about which trip is which.
  sortOrder: "asc" | "desc";
  // non-trips are hidden unless this is on
  includeNonTrips: boolean;
  onToggleIncludeNonTrips: (value: boolean) => void;
  // lifted to map-asset.tsx so clicking a route on the map can open a
  // trip's detail too
  detailedTripKey: string | null;
  onSetDetailedTrip: (tripKey: string | null) => void;
  // shared with the single-day event popup rather than a local copy
  selectedTripPoint: VehicleMapDataPoint | null;
  setSelectedTripPoint: Dispatch<SetStateAction<VehicleMapDataPoint | null>>;
};

export const TripListMultiDay: FC<Props> = ({
  dateGroups,
  selectedTripKeys,
  onToggleTripKey,
  onSetAllSelected,
  assignedDriver,
  replayingTripKey,
  replayPercentage,
  isReplaying,
  onReplayEvents,
  onReplayAll,
  hoveredTripKey,
  onHoverTrip,
  autoScrollOnHover,
  sortOrder,
  includeNonTrips,
  onToggleIncludeNonTrips,
  detailedTripKey,
  onSetDetailedTrip,
  selectedTripPoint,
  setSelectedTripPoint,
}) => {
  // collapsed by default so a full week stays scannable
  const [expandedDates, setExpandedDates] = useState<Set<string>>(
    () => new Set()
  );

  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // bring the hovered route's row into view, expanding its group first
  useEffect(() => {
    if (!hoveredTripKey || !autoScrollOnHover) return;
    const dateKey = hoveredTripKey.split("__")[0];
    setExpandedDates((prev) =>
      prev.has(dateKey) ? prev : new Set([...prev, dateKey])
    );
    const scrollToRow = () => {
      // centred rather than "nearest": a row already at the top of the list
      // doesn't move at all with nearest, so hovering a route on the map
      // highlights a row that never announces itself
      rowRefs.current
        .get(hoveredTripKey)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    // let the DOM render the row if the group just expanded
    const timer = setTimeout(scrollToRow, 50);
    return () => clearTimeout(timer);
  }, [hoveredTripKey, autoScrollOnHover]);

  // same treatment when a map click opens a trip's detail
  useEffect(() => {
    if (!detailedTripKey) return;
    const dateKey = detailedTripKey.split("__")[0];
    setExpandedDates((prev) =>
      prev.has(dateKey) ? prev : new Set([...prev, dateKey])
    );
    const timer = setTimeout(() => {
      rowRefs.current
        .get(detailedTripKey)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    return () => clearTimeout(timer);
  }, [detailedTripKey]);

  const toggleExpanded = (dateKey: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  // Trip numbers belong to trips. Non-trips used to take one each, which is
  // what had a day of three runs reading "Trip 2", "Trip 4", "Trip 6" - the
  // numbers in between went to heartbeats, and with those hidden there was
  // nothing on screen the missing numbers could refer to. Counted across the
  // whole day rather than the visible rows, so showing the non-trips doesn't
  // renumber the trips around them.
  const tripNumbersByDate = useMemo(() => {
    const byDate = new Map<string, Map<number, number>>();
    dateGroups.forEach((group) =>
      byDate.set(group.dateKey, tripDisplayNumbers(group.trips))
    );
    return byDate;
  }, [dateGroups]);

  // non-trips are dropped from the list, counts and select-all unless
  // includeNonTrips is on
  const visibleTrips = (group: DateGroup) =>
    group.trips
      .map((points, tripIndex) => ({ points, tripIndex }))
      .filter(({ points }) => includeNonTrips || !isNonTrip(points));

  // dateKey is yyyy-MM-dd, so it sorts chronologically as a plain string -
  // and unlike dayIndex it doesn't depend on the selected dates having
  // arrived in order
  const orderedGroups = useMemo(
    () =>
      [...dateGroups].sort((a, b) =>
        sortOrder === "desc"
          ? b.dateKey.localeCompare(a.dateKey)
          : a.dateKey.localeCompare(b.dateKey)
      ),
    [dateGroups, sortOrder]
  );

  // trips within a day are chronological, so latest-first is the reverse.
  // sorting days but not the trips inside them would read as half-applied.
  const orderedVisibleTrips = (group: DateGroup) => {
    const visible = visibleTrips(group);
    return sortOrder === "desc" ? [...visible].reverse() : visible;
  };

  const totalTrips = dateGroups.reduce(
    (sum, group) => sum + visibleTrips(group).length,
    0
  );

  // the whole day's figures, so the day can be read without expanding it.
  // Built off the same visible trips as the trip count beside it, so a
  // hidden non-trip is not silently counted into the totals.
  const daySummaries = useMemo(() => {
    const summaries = new Map<string, DaySummary>();
    dateGroups.forEach((group) =>
      summaries.set(
        group.dateKey,
        summariseDay(visibleTrips(group).map(({ points }) => points))
      )
    );
    return summaries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateGroups, includeNonTrips]);

  // how many trips the toggle is actually holding back. shown next to it so
  // a range with none says so, rather than leaving you to tick a box that
  // has nothing to reveal and conclude it's broken.
  const nonTripCount = dateGroups.reduce(
    (sum, group) =>
      sum + group.trips.filter((points) => isNonTrip(points)).length,
    0
  );
  const allSelected = totalTrips > 0 && selectedTripKeys.size === totalTrips;

  const dateAllSelected = (group: DateGroup) => {
    const visible = visibleTrips(group);
    return (
      visible.length > 0 &&
      visible.every(({ tripIndex }) =>
        selectedTripKeys.has(tripKeyFor(group.dateKey, tripIndex))
      )
    );
  };

  const setDateSelected = (group: DateGroup, selected: boolean) => {
    visibleTrips(group).forEach(({ tripIndex }) =>
      onToggleTripKey(tripKeyFor(group.dateKey, tripIndex), selected)
    );
  };

  return (
    <div className="flex-grow min-h-0 flex flex-col overflow-y-auto overscroll-contain">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input
            type="checkbox"
            className="size-4 accent-accent-blue-dark cursor-pointer"
            checked={allSelected}
            onChange={(e) => onSetAllSelected(e.target.checked)}
          />
          Select All Trips{" "}
          <span className="text-heading-gray888 text-xs">
            (Across all dates)
          </span>
        </label>
        <div className="flex gap-2 text-xs font-medium text-accent-blue-bright shrink-0 items-center">
          <button
            type="button"
            onClick={() => onSetAllSelected(false)}
            disabled={selectedTripKeys.size === 0}
            className="rounded-md border px-2 py-0.5 border-gray-300 text-accent-blue-dark bg-white enabled:hover:bg-accent-blue-paleO66 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Clear
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={onReplayAll}
            disabled={isReplaying || totalTrips === 0}
            className="group flex items-center gap-1 disabled:text-heading-gray888/50 enabled:hover:text-accent-blue-dark"
          >
            <img
              src={svgIcons.PlayO}
              alt=""
              className="w-3 h-3 group-disabled:grayscale group-disabled:opacity-40"
            />
            Replay All
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={() =>
              setExpandedDates(new Set(dateGroups.map((g) => g.dateKey)))
            }
            className="hover:underline"
          >
            Expand All
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={() => setExpandedDates(new Set())}
            className="hover:underline"
          >
            Collapse All
          </button>
        </div>
      </div>
      <label className="flex items-center gap-2 px-3 py-2 text-xs font-medium cursor-pointer border-b border-gray-200 text-heading-gray888">
        <input
          type="checkbox"
          className="size-3.5 accent-accent-blue-dark cursor-pointer disabled:cursor-not-allowed"
          checked={includeNonTrips}
          disabled={nonTripCount === 0}
          onChange={(e) => onToggleIncludeNonTrips(e.target.checked)}
        />
        <span className={nonTripCount === 0 ? "opacity-50" : ""}>
          Include non-trips{" "}
          <span className="text-heading-gray888">
            {nonTripCount === 0 ? "(none in range)" : `(${nonTripCount})`}
          </span>
        </span>
      </label>

      {orderedGroups.map((group) => {
        const isExpanded = expandedDates.has(group.dateKey);
        return (
          <div key={group.dateKey} className="border-b border-gray-200">
            <button
              type="button"
              onClick={() => toggleExpanded(group.dateKey)}
              className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-accent-blue-paleO66 text-left"
            >
              <span className="flex flex-col gap-1 min-w-0">
                <span className="flex items-center gap-2 font-semibold text-sm">
                  <span
                    className="inline-block size-3 rounded-full shrink-0"
                    style={{ backgroundColor: group.color }}
                  />
                  {group.label}
                  <span className="text-heading-gray888 font-normal">
                    ({visibleTrips(group).length} Trip
                    {visibleTrips(group).length === 1 ? "" : "s"})
                  </span>
                </span>
                {/* the day at a glance - same figures each trip carries,
                    added up. Hidden on a day with nothing in it, where it
                    would only read as a row of zeroes. */}
                {visibleTrips(group).length > 0 && (
                  <TripSummaryChips
                    summary={
                      daySummaries.get(group.dateKey) ?? {
                        idleCount: 0,
                        idleSeconds: 0,
                        stoppedCount: 0,
                        stoppedSeconds: 0,
                        maxSpeedKph: 0,
                      }
                    }
                    className="pl-5"
                  />
                )}
              </span>
              <img
                src={svgIcons.LeftArrowIcon}
                alt=""
                className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90" : "-rotate-90"}`}
              />
            </button>

            {isExpanded && (
              <>
                {group.isFetching ? (
                  <div className="px-3 py-4 text-xs text-heading-gray888">
                    Loading trips&hellip;
                  </div>
                ) : group.isError ? (
                  <div className="px-3 py-4 text-xs text-red-500">
                    Couldn&apos;t load trips for this date.
                  </div>
                ) : visibleTrips(group).length === 0 ? (
                  <div className="px-3 py-4 text-xs text-heading-gray888">
                    No trips on this date.
                  </div>
                ) : (
                  <>
                    <label className="flex items-center gap-2 px-3 py-2 text-xs font-medium cursor-pointer">
                      <input
                        type="checkbox"
                        className="size-4 accent-accent-blue-dark cursor-pointer"
                        checked={dateAllSelected(group)}
                        onChange={(e) =>
                          setDateSelected(group, e.target.checked)
                        }
                      />
                      Select All Trips ({visibleTrips(group).length})
                    </label>
                    {orderedVisibleTrips(group).map(({ points: tripPoints, tripIndex }) => {
                      const tripKey = tripKeyFor(group.dateKey, tripIndex);
                      return (
                        <div
                          key={tripKey}
                          ref={(el) => {
                            if (el) rowRefs.current.set(tripKey, el);
                            else rowRefs.current.delete(tripKey);
                          }}
                        >
                          <TripDetails
                            assignedDriver={assignedDriver}
                            tripNumber={
                              tripNumbersByDate
                                .get(group.dateKey)
                                ?.get(tripIndex) ?? null
                            }
                            tripPoints={tripPoints}
                            selectedTripPoint={selectedTripPoint}
                            setSelectedTripPoint={setSelectedTripPoint}
                            colorOverride={group.color}
                            checked={selectedTripKeys.has(tripKey)}
                            onCheckedChange={(checked) =>
                              onToggleTripKey(tripKey, checked)
                            }
                            selected={hoveredTripKey === tripKey}
                            onMouseOver={() => onHoverTrip(tripKey)}
                            onMouseOut={() => onHoverTrip(null)}
                            detailedView={detailedTripKey === tripKey}
                            keepClickableInDetailedView
                            onClick={() =>
                              onSetDetailedTrip(
                                detailedTripKey === tripKey ? null : tripKey
                              )
                            }
                            onReplayEvents={() => onReplayEvents(tripKey)}
                            replayInProgress={isReplaying}
                            replayPercentage={
                              replayingTripKey === tripKey
                                ? replayPercentage
                                : null
                            }
                          />
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
